import {
  FilterDropdown,
  FilterDropdownItem,
  Grid,
  GridItem,
  TableV2,
} from 'plume-ui';
import { ColumnType } from 'plume-ui/dist/components/TableV2/types';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { MixPanelEvents } from '../../../../mixPanelEvents';
import { activePartnerAtom, locationCountAtom } from '../../../../state';
import {
  AdjustableComponent,
  useClasses,
} from '../../../../utils/hooks/useClasses';
import useCriteriaNames from '../../../Monitor/hooks/useCriteriaNames';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { HealthSummaryGenericStyles } from '../../containers/HealthSummary';
import { useGetIssueMatrix } from '../../services/healthcheckServices';
import {
  getDefaultCategory,
  getIssueMatrixTableHeaders,
  IssueMatrixTableRow,
  parseIssueMatrixDataForTable,
  prepareDropdownItems,
} from './helpers';
import './IssueMatrixTable.scss';

type IssueMatrixTableProps = {
  passedClasses: HealthSummaryGenericStyles;
};

type IssueMatrixTableStyles = {
  tableContainer: string;
} & HealthSummaryGenericStyles;

const IssueMatrixTable: AdjustableComponent<
  IssueMatrixTableProps,
  IssueMatrixTableStyles
> = ({ classes, passedClasses }) => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();

  const [tableData, setTableData] = useState<IssueMatrixTableRow[]>([]);
  const [categories, setCategories] = useState<FilterDropdownItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<FilterDropdownItem>(
    getDefaultCategory(),
  );

  const activePartner = useRecoilValue(activePartnerAtom);
  const locationCount = useRecoilValue(locationCountAtom);

  const { getCriteriaName } = useCriteriaNames();

  const columns: ColumnType<IssueMatrixTableRow>[] = useMemo(
    () => getIssueMatrixTableHeaders(),
    [],
  );

  const { issueMatrixLoading, issueMatrixData } = useGetIssueMatrix(
    activePartner?.id || '',
  );

  useEffect(() => {
    if (issueMatrixLoading) return;
    if (issueMatrixData === undefined || locationCount === undefined) {
      setTableData([]);
      setCategories([]);
      return;
    }
    setTableData(
      parseIssueMatrixDataForTable(
        issueMatrixData,
        locationCount,
        selectedCategory?.value,
        getCriteriaName,
      ),
    );
    setCategories(prepareDropdownItems(issueMatrixData, getCriteriaName));
  }, [issueMatrixData, issueMatrixLoading, locationCount, selectedCategory]);

  const styles = useClasses(
    {
      root: `IssueMatrixTable`,
      title: `${passedClasses.title} IssueMatrixTable__${
        passedClasses.title.split('HealthSummary__')[1]
      }`,
      menuRow: `${passedClasses.menuRow} IssueMatrixTable__${
        passedClasses.menuRow.split('HealthSummary__')[1]
      }`,
      summaryCell: `${passedClasses.summaryCell} IssueMatrixTable__${
        passedClasses.summaryCell.split('HealthSummary__')[1]
      }`,
      tableContainer: `IssueMatrixTable__table-container`,
    },
    classes,
  );

  return (
    <div className={styles.root}>
      <Grid>
        <GridItem colSpan="12" tabletColSpan="12" className={styles.menuRow}>
          <FilterDropdown
            defaultLabel={t('all')}
            containerLabel={t('healthSummary.issueMatrixTable.dropdownLabel')}
            onSelect={(item) => {
              trackEvent({
                eventName:
                  MixPanelEvents.HEALTH_SUMMARY_ISSUE_MATRIX_CATEGORY_SELECT,
              });
              setSelectedCategory(item);
            }}
            closeOnItemClick={true}
            items={categories}
            selectedItem={selectedCategory}
            openInPortal
          />
        </GridItem>

        <GridItem colSpan="12" tabletColSpan="12">
          <div className={styles.summaryCell}>
            <div className={styles.tableContainer}>
              <TableV2
                data={tableData}
                columns={columns}
                stickyHeader
                rowKey={(row) => `${row.issueCategory}-${row.issueType}`}
              />
            </div>
          </div>
        </GridItem>
      </Grid>
    </div>
  );
};

export default memo(IssueMatrixTable);
